import React, { useEffect, useState } from 'react';
import { Link as RouterLink, useLocation, useParams } from 'react-router-dom';

import { useDispatch, useSelector } from 'react-redux';
import { FormattedDate, useIntl } from 'react-intl';
import {
  Avatar,
  Box,
  Breadcrumbs,
  Button,
  CircularProgress,
  Link,
  Menu,
  MenuItem,
  Typography,
  Hidden,
} from '@mui/material';
import { EditOutlined } from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { useAbility } from '@casl/react';
import messages from './messages';
import commonMessages from '../common/messages';
import { makeSelectEvent } from '../../managers/EventDetailManager/selectors';
import {
  makeSelectTeamBibNumber,
  makeSelectTeamId,
  makeSelectTeamName,
  makeSelectTeamRegistrationDate,
  makeSelectTeamStatus,
  makeSelectTeamStatusState,
} from '../../managers/EventTeamDetailManager/selectors';
import { updateTeamStatus } from '../../managers/EventTeamDetailManager/actions';
import {
  formDisplayName,
  getColorBasedOnStatus,
} from '../../utils/common.util';
import { makeSelectVolunteer } from '../../managers/VolunteerDetailManager/selectors';
import { selectMember } from '../../managers/MemberDetailManager/selectors';
import { makeSelectEventGroup } from '../../managers/EventGroupDetailManager/selectors';

import {
  TEAM_STATUS_OPTIONS as teamStatusOptions,
  getLabelOfSelectedTeamStatus,
} from '../../utils/options.util';
import { ActiveEventInfo } from './ActiveEventInfo';
import {
  makeSelectSupporterDisplayName,
  makeSelectSupporterTeamDetail,
} from '../../managers/SupporterDetailManager/selectors';
import ActiveEventGroupInfo from './ActiveEventGroupInfo';
import { AbilityContext } from '../../casl/AbilityContext';
import { useTitleManager } from '../../hooks/useTitleManager';
import SEOTitle from '../common/SEOTitle';

export function DynamicHeaderTitle() {
  const intl = useIntl();
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const { id, eventId, supporterId } = useParams();
  const { pathname } = useLocation();
  const isEventGroupRoute = pathname.includes('event-group');
  const actualEventId = isEventGroupRoute ? eventId : id;
  const eventResourcePathPrefix = isEventGroupRoute
    ? `/event-group/${id}/events/${actualEventId}`
    : `/event/${actualEventId}`;
  const ability = useAbility(AbilityContext);
  const canViewTeam = ability.can('read', 'Team');
  const canUpdateTeam = ability.can('update', 'Team');
  const { routeConfig, getTitleText, getSEOTitle } = useTitleManager();
  const eventDetail = useSelector(makeSelectEvent());
  const teamName = useSelector(makeSelectTeamName());
  const teamId = useSelector(makeSelectTeamId());
  const teamRegistrationDate = useSelector(makeSelectTeamRegistrationDate());
  const teamBibNumber = useSelector(makeSelectTeamBibNumber());
  const teamActualStatus = useSelector(makeSelectTeamStatus());
  const verifyState = useSelector(makeSelectTeamStatusState());

  const [teamStatus, setTeamStatus] = useState('');
  const [verificationAnchorEl, setVerificationAnchorEl] = useState(null);
  const openVerificationMenu = Boolean(verificationAnchorEl);

  const volunteerDetail = useSelector(makeSelectVolunteer);
  const supporterDisplayName = useSelector(
    makeSelectSupporterDisplayName(supporterId),
  );
  const supporterTeamDetail = useSelector(
    makeSelectSupporterTeamDetail(supporterId),
  );
  const memberDetail = useSelector(selectMember);
  const eventGroupDetail = useSelector(makeSelectEventGroup);

  const openVerificationSelection = event => {
    setVerificationAnchorEl(event.currentTarget);
  };

  const handleVerificationItemClick = state => {
    setTeamStatus(state);
    setVerificationAnchorEl(null);
    dispatch(
      updateTeamStatus({
        id: teamId,
        data: {
          value: state,
        },
      }),
    );
  };

  const handleVerificationClose = () => {
    setVerificationAnchorEl(null);
  };

  useEffect(() => {
    if (verifyState === 2) {
      enqueueSnackbar(intl.formatMessage(messages.updateTeamStatusSuccess), {
        variant: 'success',
      });
    } else if (verifyState === 3) {
      setTeamStatus(teamActualStatus);
    }
  }, [verifyState, teamActualStatus, enqueueSnackbar, intl]);

  useEffect(() => {
    setTeamStatus(teamActualStatus);
  }, [teamActualStatus]);

  // Route-based title rendering
  const renderTitle = () => {
    const { title, component, showActiveEvent, isEventGroup } = routeConfig;

    // Handle custom components with dynamic content
    if (component) {
      return renderCustomComponent(component, showActiveEvent, isEventGroup);
    }

    // Handle standard titles
    return renderStandardTitle(title, showActiveEvent, isEventGroup);
  };

  const renderStandardTitle = (title, showActiveEvent, isEventGroup) => (
    <>
      <Hidden mdDown>
        <Typography
          component="h1"
          variant="h6"
          color="inherit"
          noWrap
          sx={{ flexGrow: showActiveEvent ? 'unset' : 1 }}
          fontWeight="bold"
        >
          {getTitleText(title)}
        </Typography>
      </Hidden>
      {showActiveEvent &&
        (isEventGroup ? <ActiveEventGroupInfo /> : <ActiveEventInfo />)}
    </>
  );

  const renderCustomComponent = (component, showActiveEvent, isEventGroup) => {
    switch (component) {
      case 'VolunteerDetail':
        return (
          <>
            {volunteerDetail && eventDetail && (
              <Breadcrumbs aria-label="breadcrumb">
                <Link
                  underline="hover"
                  component={RouterLink}
                  to={`${eventResourcePathPrefix}/volunteers`}
                  color="inherit"
                >
                  {intl.formatMessage(messages.eventVolunteerList)}
                </Link>
                <Typography
                  component="h1"
                  variant="h6"
                  noWrap
                  fontWeight="bold"
                  color="text.primary"
                >
                  {volunteerDetail &&
                    formDisplayName(
                      volunteerDetail.firstName,
                      '',
                      volunteerDetail.lastName,
                    )}
                </Typography>
              </Breadcrumbs>
            )}
            {isEventGroup ? <ActiveEventGroupInfo /> : <ActiveEventInfo />}
          </>
        );
      case 'SupporterDetail':
        return (
          <>
            {supporterDisplayName && supporterTeamDetail && eventDetail && (
              <Breadcrumbs aria-label="breadcrumb">
                <Link
                  underline="hover"
                  component={RouterLink}
                  to={`${eventResourcePathPrefix}/teams/${supporterTeamDetail.id}`}
                  color="inherit"
                >
                  {supporterTeamDetail.name}
                </Link>
                <Typography
                  component="h1"
                  variant="h6"
                  noWrap
                  fontWeight="bold"
                  color="text.primary"
                >
                  {supporterDisplayName}
                </Typography>
              </Breadcrumbs>
            )}
            {isEventGroup ? <ActiveEventGroupInfo /> : <ActiveEventInfo />}
          </>
        );
      case 'MemberDetail':
        return (
          <>
            {memberDetail && (
              <Breadcrumbs aria-label="breadcrumb">
                {canViewTeam ? (
                  <Link
                    underline="hover"
                    component={RouterLink}
                    to={`${eventResourcePathPrefix}/teams/${memberDetail.team.id}`}
                    color="inherit"
                  >
                    {memberDetail.team.name}
                  </Link>
                ) : (
                  <Typography color="inherit">
                    {memberDetail.team.name}
                  </Typography>
                )}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Avatar src={memberDetail.avatarUrl}></Avatar>
                  <Typography
                    component="h1"
                    variant="h6"
                    noWrap
                    fontWeight="bold"
                    color="text.primary"
                  >
                    {formDisplayName(
                      memberDetail.firstName,
                      '',
                      memberDetail.lastName,
                    )}
                  </Typography>
                </Box>
              </Breadcrumbs>
            )}
            {isEventGroup ? <ActiveEventGroupInfo /> : <ActiveEventInfo />}
          </>
        );
      case 'TeamDetail':
        return (
          <>
            <div>
              {teamRegistrationDate ? (
                <Typography
                  variant="caption"
                  color="primary.main"
                  noWrap
                  sx={{ letterSpacing: '0.04em' }}
                >
                  <FormattedDate
                    value={teamRegistrationDate}
                    day="numeric"
                    month="long"
                    year="numeric"
                  />
                </Typography>
              ) : null}
              <Box sx={{ display: 'flex' }}>
                <Typography
                  component="span"
                  variant="h6"
                  color="inherit"
                  noWrap
                  fontWeight="bold"
                >
                  {teamName}
                </Typography>
                {teamBibNumber ? (
                  <Hidden mdDown>
                    <Typography
                      component="span"
                      variant="h6"
                      color="inherit"
                      noWrap
                    >
                      / {intl.formatMessage(messages.bib)} {teamBibNumber}
                    </Typography>
                  </Hidden>
                ) : null}
                {/* team detail status change */}
                {teamStatus ? (
                  <Box sx={{ ml: 1 }}>
                    <Button
                      disableRipple
                      onClick={openVerificationSelection}
                      color={getColorBasedOnStatus(teamStatus)}
                      variant="contained"
                      size="small"
                      aria-haspopup="listbox"
                      aria-controls="team-verify-menu"
                      aria-label="Team verification workflow"
                      aria-expanded={openVerificationMenu ? 'true' : undefined}
                      disabled={verifyState === 1 || !canUpdateTeam}
                    >
                      <span>
                        {messages[getLabelOfSelectedTeamStatus(teamStatus)]
                          ? intl.formatMessage(
                              messages[
                                getLabelOfSelectedTeamStatus(teamStatus)
                              ],
                            )
                          : teamStatus}
                      </span>
                      {verifyState === 1 ? (
                        <CircularProgress size={20} color="inherit" />
                      ) : (
                        <EditOutlined fontSize="small" />
                      )}
                    </Button>
                    <Menu
                      id="team-verify-menu"
                      anchorEl={verificationAnchorEl}
                      open={openVerificationMenu}
                      onClose={handleVerificationClose}
                      MenuListProps={{
                        role: 'listbox',
                      }}
                    >
                      {teamStatusOptions.map(option => (
                        <MenuItem
                          key={option.val}
                          selected={teamStatus === option.val}
                          onClick={() =>
                            handleVerificationItemClick(option.val)
                          }
                          color="danger"
                        >
                          {intl.formatMessage(commonMessages[option.label])}
                        </MenuItem>
                      ))}
                    </Menu>
                  </Box>
                ) : null}
              </Box>
            </div>
            {isEventGroup ? <ActiveEventGroupInfo /> : <ActiveEventInfo />}
          </>
        );
      case 'EventGroupHome':
        return renderStandardTitle('eventHome', true, isEventGroup);
      default:
        return renderStandardTitle(
          'welcomeText',
          showActiveEvent,
          isEventGroup,
        );
    }
  };

  // Get dynamic title for EventGroupHome
  const getDynamicSEOTitle = () => {
    if (routeConfig.component === 'EventGroupHome' && eventGroupDetail?.name) {
      return getSEOTitle(routeConfig.title, eventGroupDetail.name);
    }
    return getSEOTitle();
  };

  return (
    <>
      <SEOTitle title={getDynamicSEOTitle()} />
      {renderTitle()}
    </>
  );
}
